import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
} from "@mui/material";
import React, { useMemo } from "react";
import ReactTimeAgo from "react-time-ago";

import {
  CONTENT_CONFIDENCE_THRESHOLD_SYS_ACC,
  contentProjectedAccuracy,
} from "components/detector/LineChartMetrics/ContentStrings";
import { useAppSelector } from "hooks/store";
import { Typography } from "new-components/typography/Typography";
import { getUserState } from "store/slices/Auth/authSlice";
import { DetectorModes } from "types/createDetector";
import { Detector, EvaluationResults } from "types/detectors";
import { scaleNumber } from "utils/math";
import { convertUTCtoLocalReadableDate, generateTicks, parseDateAsUTC } from "utils/utilityFunctions";

import AccuracyDetails, { AccuracyData } from "./AccuracyDetails";
import { mapColorBar, mapOpacityColorBar } from "./chartUtils";

export interface ProjectedAccuracyModalProps {
  isTooltipModalOpen: boolean;
  handleTooltipModalClose: () => void;
  detectorData: Detector;
}

export function getAccuracyByThreshold(
  data:
    | EvaluationResults[
        | "balanced_system_accuracies"
        | "ml_answer_proportions"
        | "negative_system_accuracies"
        | "positive_system_accuracies"]
    | undefined,
  threshold: number,
  fallbackValue = true
) {
  if (!data) return fallbackValue ? 0.5 : undefined;

  return Object.entries(data)
    .filter(([key]) => key.includes("__mean"))
    .find(([key]) => {
      const match = key.match(/__([\d.]+)__/);
      return match && parseFloat(match[1]) === threshold;
    })?.[1];
}

export function ProjectedAccuracyModal(props: ProjectedAccuracyModalProps) {
  const { handleTooltipModalClose, isTooltipModalOpen, detectorData } = props;
  const { show_system_accuracy: showSysAcc } = useAppSelector(getUserState);
  const isEvaluatingResults = useMemo(() => {
    // Is evaluating the detector evaluating the last ground truth.
    // It helps to show the evaluating text and spinner
    if (detectorData.evaluation_results !== null && detectorData.recent_ground_truth_label_date !== null) {
      const lastEvalTimestamp = parseDateAsUTC(detectorData.evaluation_results.eval_timestamp);
      const recentGroundTruthLabelDate = parseDateAsUTC(detectorData.recent_ground_truth_label_date);
      return lastEvalTimestamp < recentGroundTruthLabelDate;
    }
    return false;
  }, [detectorData]);

  const escalationsResults = useMemo(() => detectorData.evaluation_results ?? null, [detectorData]);

  // Converts the performance data to an array with additional fields
  // needed for the chart visualization.
  const mlAccuracyData: AccuracyData[] = useMemo(() => {
    if (escalationsResults !== null) {
      const tableData = escalationsResults;
      const createData: AccuracyData[] = [
        {
          plotData: {
            name: "ml_accuracy",
            value: scaleNumber(tableData.kfold_pooled__balanced_accuracy),
            quartiles: [
              scaleNumber(tableData.kfold_pooled__balanced_accuracy__lcb),
              scaleNumber(tableData.kfold_pooled__balanced_accuracy__ucb),
            ],
            colorBar: mapColorBar.ml_accuracy,
            opacityColorBar: mapOpacityColorBar.ml_accuracy,
          },
          label: "Balanced ML Accuracy",
          totalGroundTruth: escalationsResults?.total_ground_truth_examples || 0,
          totalLabeled: escalationsResults?.total_labeled_examples || 0,
          isMissingDataRequirements: false,
        },
        ...(detectorData.mode === DetectorModes.BINARY
          ? [
              {
                plotData: {
                  name: "ml_accuracy_yes",
                  value: scaleNumber(tableData.kfold_pooled__negative_accuracy),
                  quartiles: [
                    scaleNumber(tableData.kfold_pooled__negative_accuracy__lcb),
                    scaleNumber(tableData.kfold_pooled__negative_accuracy__ucb),
                  ],
                  colorBar: mapColorBar.ml_accuracy_yes,
                  opacityColorBar: mapOpacityColorBar.ml_accuracy_yes,
                },
                label: "ML Accuracy for “YES”",
                totalGroundTruth: escalationsResults?.total_negative_ground_truth_examples || 0,
                totalLabeled: escalationsResults?.total_negative_labeled_examples || 0,
                isMissingDataRequirements: false,
              },
              {
                plotData: {
                  name: "ml_accuracy_no",
                  value: scaleNumber(tableData.kfold_pooled__positive_accuracy),
                  quartiles: [
                    scaleNumber(tableData.kfold_pooled__positive_accuracy__lcb),
                    scaleNumber(tableData.kfold_pooled__positive_accuracy__ucb),
                  ],
                  colorBar: mapColorBar.ml_accuracy_no,
                  opacityColorBar: mapOpacityColorBar.ml_accuracy_no,
                },

                label: "ML Accuracy for “NO”",
                totalGroundTruth: escalationsResults?.total_positive_ground_truth_examples || 0,
                totalLabeled: escalationsResults?.total_positive_labeled_examples || 0,
                isMissingDataRequirements: false,
              },
            ]
          : []),
        ...(detectorData.mode === DetectorModes.MULTICHOICE
          ? detectorData.mode_configuration?.class_names?.map((className, index) => ({
              plotData: {
                name: `ml_accuracy_${className}`,
                value: tableData.class_accuracies
                  ? scaleNumber(tableData.class_accuracies[`class_accuracies__${index}__mean`])
                  : 0,
                quartiles: [],
                colorBar: mapColorBar.ml_accuracy_multi_choice,
                opacityColorBar: mapOpacityColorBar.ml_accuracy_multi_choice,
              },
              label: `ML Accuracy for “${className}”`,
              totalGroundTruth: escalationsResults?.total_per_class_ground_truth_examples[`${index}`],
              totalLabeled: escalationsResults?.total_per_class_labeled_examples[`${index}`],
              isMissingDataRequirements: tableData.class_accuracies?.[`class_accuracies__${index}__mean`] === undefined,
            })) ?? []
          : []),
      ];
      return createData;
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escalationsResults?.kfold_pooled__balanced_accuracy]);

  const systemAccuracyData: AccuracyData[] = useMemo(() => {
    if (escalationsResults === null || !showSysAcc) return [];
    const tableData: any = escalationsResults;
    return [
      {
        label: "Balanced System Accuracy",
        totalGroundTruth: escalationsResults?.total_ground_truth_examples || 0,
        totalLabeled: escalationsResults?.total_labeled_examples || 0,
        plotData: {
          name: "sys_accuracy",
          value: scaleNumber(
            getAccuracyByThreshold(tableData?.balanced_system_accuracies, detectorData.confidence_threshold ?? 0) ?? 0
          ),
          colorBar: mapColorBar.ml_accuracy,
          opacityColorBar: mapOpacityColorBar.ml_accuracy,
          quartiles: [],
        },
        isMissingDataRequirements: false,
      },
      {
        label: "System Accuracy for “YES”",
        totalGroundTruth: escalationsResults?.total_negative_ground_truth_examples || 0,
        totalLabeled: escalationsResults?.total_negative_labeled_examples || 0,
        plotData: {
          name: "sys_accuracy_yes",
          value: scaleNumber(
            getAccuracyByThreshold(tableData?.negative_system_accuracies, detectorData.confidence_threshold ?? 0) ?? 0
          ),
          colorBar: mapColorBar.ml_accuracy_yes,
          opacityColorBar: mapOpacityColorBar.ml_accuracy_yes,
          quartiles: [],
        },
        isMissingDataRequirements: false,
      },
      {
        label: "System Accuracy for “NO”",
        totalGroundTruth: escalationsResults?.total_positive_ground_truth_examples || 0,
        totalLabeled: escalationsResults?.total_positive_labeled_examples || 0,
        plotData: {
          name: "sys_accuracy_no",
          value: scaleNumber(
            getAccuracyByThreshold(tableData?.positive_system_accuracies, detectorData.confidence_threshold ?? 0) ?? 0
          ),
          colorBar: mapColorBar.ml_accuracy_no,
          opacityColorBar: mapOpacityColorBar.ml_accuracy_no,
          quartiles: [],
        },
        isMissingDataRequirements: false,
      },
    ] as AccuracyData[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escalationsResults?.kfold_pooled__balanced_accuracy]);

  // Provides custom tick values for the chart.
  const ticksForChart: number[] = useMemo(() => {
    if (mlAccuracyData.length > 0) {
      return generateTicks(mlAccuracyData?.map((item) => item.plotData));
    }
    return [50, 60, 70, 80, 90, 100];
  }, [mlAccuracyData]);

  const canSeeProjectedSystemAccuracyDetails = showSysAcc && detectorData.mode === DetectorModes.BINARY;

  return (
    <Dialog
      open={isTooltipModalOpen}
      onClose={handleTooltipModalClose}
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      PaperProps={{
        className: "min-w-[95vw] max-w-[95vw] md:min-w-[75vw] md:max-w-[75vw] bg-[#F3F5F6]",
      }}
    >
      <DialogTitle
        id="scroll-dialog-title"
        className="flex h-[50px] justify-between items-center bg-[#E6E9ED] px-6 py-0"
      >
        <Box className="flex items-center">
          <Typography id="modal-modal-title" variant="subtitle" className="text-[#0450BA]">
            <b>{detectorData.name}:</b> Accuracy Details
          </Typography>
        </Box>
        <IconButton onClick={handleTooltipModalClose} className="p-0">
          <HighlightOffIcon className="text-gray-600" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!mlAccuracyData ? (
          <Skeleton height="128px" />
        ) : (
          <Box className="bg-white flex justify-center w-full my-6 p-4 rounded-lg">
            <Box className="flex justify-center items-center gap-6 px-4">
              <ul className="flex justify-normal md:justify-center flex-wrap gap-1 md:gap-6 list-disc">
                <Typography component="li" variant="detail" className="text-[#006DD9] mx-2 md:mx-0">
                  Evaluation Date:{" "}
                  {escalationsResults?.eval_timestamp
                    ? convertUTCtoLocalReadableDate(escalationsResults?.eval_timestamp)
                    : "-"}
                  {isEvaluatingResults ? (
                    <Typography component="span" variant="detail" className="ml-1 text-[#006DD9]">
                      (evaluating
                      <CircularProgress className="ml-1" size={10} />
                      ).
                    </Typography>
                  ) : (
                    "."
                  )}
                </Typography>
                <Typography component="li" variant="detail" className="text-[#006DD9] mx-2 md:mx-0">
                  Based on {escalationsResults?.total_ground_truth_examples || "-"} Ground Truth labels.
                </Typography>
                {detectorData.recent_ground_truth_label_date && (
                  <Typography component="li" variant="detail" className="text-[#006DD9] mx-2 md:mx-0">
                    Most recent Ground Truth label was{" "}
                    <ReactTimeAgo date={parseDateAsUTC(detectorData.recent_ground_truth_label_date)} locale="en-US" />.
                  </Typography>
                )}
              </ul>
            </Box>
          </Box>
        )}

        {canSeeProjectedSystemAccuracyDetails && (
          <AccuracyDetails
            accuracyTitle="PROJECTED SYSTEM ACCURACY DETAILS:"
            XAxisTicks={ticksForChart}
            data={systemAccuracyData}
            infoTooltipTitle="Projected System Accuracy"
            infoTooltipContent={CONTENT_CONFIDENCE_THRESHOLD_SYS_ACC}
          />
        )}

        <AccuracyDetails
          accuracyTitle="PROJECTED ML ACCURACY DETAILS:"
          XAxisTicks={ticksForChart}
          infoTooltipTitle="Projected ML Accuracy"
          infoTooltipContent={contentProjectedAccuracy}
          data={mlAccuracyData}
        />
      </DialogContent>

      <DialogActions className="h-[50px] bg-[#E6E9ED] px-6 py-0 justify-start">
        <Typography id="modal-modal-title" variant="detail" className="text-black">
          Detector ID: {detectorData.id}
        </Typography>
      </DialogActions>
    </Dialog>
  );
}
